import {
  getSdkToken as fetchSdkToken,
  getGetSdkTokenQueryKey,
  useGetSdkToken,
} from '@/api/generated/endpoints/dashboard';
import { CancelledError } from '@tanstack/react-query';
import type { QueryClient, UseQueryOptions } from '@tanstack/react-query';
import { DashboardLevel, LicenseLevel, SDKTokenResponseSchema } from '@/api/generated/schemas';
import { ErrorType } from '../mutators/IgniteAxiosClient';
import { captureException } from '@sentry/react';
import { useEffect, useMemo } from 'react';
import { useFlag } from '@alchemer/unleash';
import { FEATURE_FLAGS } from '@/config/features';
import {
  creatorConfig,
  customShareCustomActions,
  customShareHiddenActions,
  nonCreatorConfig,
} from '@/customizations/thoughtspotCustomizations';
import { Action, AppViewConfig, HomePageSearchBarMode } from '@thoughtspot/visual-embed-sdk';
import { tokenStorage } from '@/lib/tokenStorage';
import { SDK_TOKEN } from '@/constants/queries';

// Module-level variable to store pending fetch promise for deduplication
let pendingTokenFetch: Promise<string> | null = null;
interface SdkTokenErrorData {
  message?: string;
}

interface UseSdkTokenQueryParams {
  enabled: boolean;
  options?: Partial<UseQueryOptions<SDKTokenResponseSchema, ErrorType<SdkTokenErrorData>, SDKTokenResponseSchema>>;
}

export function useSdkTokenQuery({ enabled, options }: UseSdkTokenQueryParams) {
  const query = useGetSdkToken({
    query: {
      staleTime: SDK_TOKEN.STALE_TIME_MS,
      refetchInterval: SDK_TOKEN.REFETCH_INTERVAL_MS,
      refetchIntervalInBackground: true,
      retry: false,
      enabled: enabled,
      ...options,
    },
  });

  // Persist token to sessionStorage when data changes
  // This handles both initial fetch and background refreshes
  useEffect(() => {
    if (query.data && query.status === 'success') {
      tokenStorage.setToken(query.data);
    }
  }, [query.data, query.dataUpdatedAt, query.status]);

  return query;
}

/**
 * Hook to get SDK token data from React Query cache without triggering a fetch.
 * This is the base hook used by all other derived hooks.
 * The actual query is managed by RootLayout component.
 */
const useSdkTokenData = () => {
  const { data } = useGetSdkToken({
    query: {
      enabled: false, // Never fetches, only reads cache
      staleTime: SDK_TOKEN.STALE_TIME_MS,
    },
  });
  return data;
};

/**
 * Hook to get the SDK token string from React Query cache
 */
export const useSdkToken = () => {
  const data = useSdkTokenData();
  return data?.sdkToken ?? null;
};

/**
 * Hook to get the license level from React Query cache
 */
export const useLicenseLevel = () => {
  const data = useSdkTokenData();
  return data?.licenseLevel ?? null;
};

/**
 * Hook to get app permissions from React Query cache
 */
export const useAppPermissions = () => {
  const data = useSdkTokenData();
  return useMemo(
    () => ({
      workflow: data?.hasWorkflow ?? false,
      pulse: data?.hasPulse ?? false,
      digital: data?.hasDigital ?? false,
      // TODO: change with 'data?.reputation ?? false' whenever backend support arrives
      reputation: true,
      // TODO: change with 'data?.listings ?? false' whenever backend support arrives
      listings: true,
    }),
    [data?.hasWorkflow, data?.hasPulse, data?.hasDigital],
  );
};

/**
 * Hook to get dashboard level from React Query cache
 */
export const useDashboardLevel = () => {
  const data = useSdkTokenData();
  return data?.dashboardLevel ?? null;
};

/**
 * Hook to check if user is a creator (derived from license level)
 */
export const useIsCreator = () => {
  const licenseLevel = useLicenseLevel();
  return licenseLevel === LicenseLevel.Creator;
};

/**
 * Hook to check if user has advanced dashboard level
 */
export const useIsAdvanced = () => {
  const dashboardLevel = useDashboardLevel();
  return dashboardLevel === DashboardLevel.Advanced;
};

// SpotterViz is shown to users as SparkViz (see stringOverrides); also gated by the Spark flag
const ADVANCED_ONLY_ACTIONS: Action[] = [Action.AIHighlights, Action.SpotterViz];

/**
 * Derive user app view config from license and dashboard levels.
 * Creates a new config object without mutating the base configs.
 */
const deriveUserAppViewConfig = (
  licenseLevel: LicenseLevel | null,
  dashboardLevel: DashboardLevel | null,
  customShareEnabled: boolean,
  sparkEnabled: boolean,
): AppViewConfig => {
  const isCreator = licenseLevel === LicenseLevel.Creator;
  const baseConfig = isCreator ? creatorConfig : nonCreatorConfig;
  const mode =
    dashboardLevel === DashboardLevel.Advanced ? HomePageSearchBarMode.AI_ANSWER : HomePageSearchBarMode.OBJECT_SEARCH;

  // Actions only available to dashboardLevel Advanced; new arrays avoid mutating shared config
  let hiddenActions =
    dashboardLevel === DashboardLevel.Advanced
      ? (baseConfig.hiddenActions ?? []).filter((action) => !ADVANCED_ONLY_ACTIONS.includes(action))
      : [...new Set([...(baseConfig.hiddenActions ?? []), ...ADVANCED_ONLY_ACTIONS])];
  if (!sparkEnabled && !hiddenActions.includes(Action.SpotterViz)) {
    hiddenActions = [...hiddenActions, Action.SpotterViz];
  }

  // Custom share: hide native share buttons and inject custom action buttons for creators
  const customActions = isCreator && customShareEnabled ? customShareCustomActions : undefined;
  if (isCreator && customShareEnabled) {
    hiddenActions = [...hiddenActions, ...customShareHiddenActions];
  }

  return {
    ...baseConfig,
    hiddenActions,
    homePageSearchBarMode: mode,
    ...(customActions && { customActions }),
  };
};

/**
 * Hook to get the derived user app view config
 */
export const useUserAppViewConfig = () => {
  const licenseLevel = useLicenseLevel();
  const dashboardLevel = useDashboardLevel();
  const customShareEnabled = useFlag(FEATURE_FLAGS.CUSTOM_SHARE);
  const sparkEnabled = useFlag(FEATURE_FLAGS.SPARK);
  return useMemo(
    () => deriveUserAppViewConfig(licenseLevel, dashboardLevel, customShareEnabled, sparkEnabled),
    [licenseLevel, dashboardLevel, customShareEnabled, sparkEnabled],
  );
};

/**
 * Get SDK token from React Query cache synchronously (for non-React code).
 * Only returns token if query is in success state with fresh data.
 */
export const getSdkTokenFromCache = (queryClient: QueryClient): string | undefined => {
  const queryKey = getGetSdkTokenQueryKey();
  const queryState = queryClient.getQueryState<SDKTokenResponseSchema>(queryKey);

  // Only return if query is in success state and data exists
  if (queryState?.status === 'success' && queryState?.data) {
    return queryState.data.sdkToken;
  }

  return undefined;
};

/**
 * Fetches the SDK token and returns it as a promise.
 * This function first checks sessionStorage, then React Query cache, and only fetches if not cached.
 * Concurrent calls are deduplicated - all callers receive the same promise.
 * This function is intended to be used with the ThoughtSpot SDK's getAuthToken parameter.
 * @param queryClient - The React Query client instance
 * @returns A promise that resolves with the SDK token string, or throws an error if fetch fails.
 */
export const getSdkTokenPromise = async (queryClient: QueryClient): Promise<string> => {
  // Return existing promise if fetch is in progress (deduplication)
  if (pendingTokenFetch) {
    return pendingTokenFetch;
  }

  // 1. Check sessionStorage first (synchronous, no race condition)
  const storedToken = tokenStorage.getToken();
  if (storedToken?.sdkToken && tokenStorage.isTokenValid() && !tokenStorage.needsRefresh()) {
    return storedToken.sdkToken;
  }

  // 2. Check React Query cache
  const cachedToken = getSdkTokenFromCache(queryClient);
  if (cachedToken) {
    return cachedToken;
  }

  // 3. Fetch from API (only if no fetch in progress)
  pendingTokenFetch = (async () => {
    try {
      // Use queryClient.fetchQuery to respect React Query configuration and cache
      const data = await queryClient.fetchQuery({
        queryKey: getGetSdkTokenQueryKey(),
        queryFn: () => fetchSdkToken(),
        staleTime: SDK_TOKEN.STALE_TIME_MS,
      });

      tokenStorage.setToken(data);
      return data.sdkToken;
    } catch (error) {
      if (!(error instanceof CancelledError)) {
        captureException(error, {
          level: 'error',
          extra: {
            message: 'Failed to fetch SDK token',
          },
        });
      }

      throw error;
    } finally {
      // Clear pending promise after completion (success or failure)
      pendingTokenFetch = null;
    }
  })();

  return pendingTokenFetch;
};
